import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import Button from "core/components/Button";
import Field from "core/components/Field";
import Flex from "core/components/Flex";
import {
  Panel,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from "core/components/Panel";
import { z } from "zod";
import {
  minLength,
  maxLength,
  required,
  email,
  validate,
  domain,
  objectOrArrayRequired,
  noDuplicateArrayItems,
  startsWithLetter,
  lowercase,
  lettersAndNumbers,
  notEquals,
} from "common/validation";
import useForm from "core/hooks/useForm";
import useAuthenticatedCall from "core/hooks/useAuthenticatedCall";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import { getCustomerSegments } from "modules/admin/actions";
import { DEFAULT_ORG } from "core/constants";
import {
  createOrganization,
  updateOrganizationLicenses,
} from "modules/admin/actions";
import Error from "core/components/Error";
import OrgIdTooltip from "./OrgIdTooltip";
import CustomerSegmentTooltip from "./CustomerSegmentTooltip";
import { toast } from "sonner";

const formSchema = z.object({
  id: z.string().min(2).max(10),
  active: z.boolean(),
  name: z.string().min(2),
  domain: z.array(z.string().min(3)).min(1),
  customer_segment: z.string().min(1),
  medicare_licensed: z.boolean(),
  primaryContact: z.object({
    name: z.string().min(2),
    email: z.email(),
  }),
  licenses: z.object({
    tableau_user_licenses: z.object({
      explorer_count: z.number(),
      viewer_count: z.number(),
    }),
    user_limit: z.number(),
    analytics_hub_user_licenses: z.object({
      pro_count: z.number(),
    }),
  }),
  preferences: z.object({
    bi_portal: z.object({
      default_states: z.array(z.string()),
    }),
  }),
});

const OrgDetailsForm = ({
  onSubmit,
  initialValues,
  isFetching = false,
  isSubmitting,
  error,
  onClose,
}) => {
  const {
    formValues,
    errors,
    onFieldChange,
    onFormSubmit,
    onFormReset,
    isDisabled,
    setIsFormDisabled,
  } = useForm({
    initialValues,
    formSchema,
    onSubmit,
    disabledUntilTouched: true,
    isFetchingInitialValues: isFetching,
  });

  const fetchCustomerSegments = useAuthenticatedCall(getCustomerSegments);
  const customerSegmentsQueryKey = ["customer-segments"];

  const { data: customerSegments, isLoading: isLoadingSegments } = useQuery({
    queryKey: customerSegmentsQueryKey,
    queryFn: fetchCustomerSegments,
    placeholderData: {},
  });

  const getCustomerSegmentOptions = useMemo(() => {
    let obj = [];

    if (customerSegments && Object.keys(customerSegments).length > 0) {
      const customerSegmentKeys = Object.keys(customerSegments);
      for (let i = 0; i < customerSegmentKeys.length; i++) {
        const option = {
          value: customerSegmentKeys[i],
          label: Object.values(customerSegments).find(
            (item) => item.id === customerSegmentKeys[i]
          )?.name,
        };
        obj.push(option);
      }
    }

    return obj;
  }, [customerSegments]);

  const isMedicareLicensable = Object.values(customerSegments).find(
    (item) => item.id === formValues.customer_segment
  )?.is_medicare_licensable;

  const handleCancel = () => {
    onFormReset();
    onClose();
  };

  return (
    <Flex direction="column" gap="24px">
      <Flex direction="column" style={{ borderBottom: "1px solid #898d8f" }}>
        <Error>{error}</Error>
        <Field
          type="text"
          id="name"
          labelText="Organization Name"
          placeholder="Enter name"
          isRequired={true}
          isDisabled={isSubmitting}
          value={formValues.name}
          error={errors.name}
          onChange={(value, errorMessage) => {
            onFieldChange("name")(value, errorMessage);
          }}
          validator={validate(required(), minLength(2))}
        />
        <Field
          type="text"
          id="id"
          labelText="Organization ID"
          labelHint={<OrgIdTooltip />}
          placeholder="Enter unique id"
          isRequired={true}
          isDisabled={isSubmitting}
          value={formValues.id}
          error={errors.id}
          onChange={(value, errorMessage) => {
            onFieldChange("id")(value, errorMessage);
          }}
          validator={validate(
            required(),
            startsWithLetter(),
            lowercase(),
            lettersAndNumbers(),
            minLength(2),
            maxLength(10),
            notEquals("hosting", "populi")
          )}
        />
        <Field
          type="text"
          id="primaryContact.name"
          labelText="Primary Contact"
          placeholder="Enter name"
          isRequired={true}
          isDisabled={isSubmitting}
          value={formValues.primaryContact?.name}
          error={errors.primaryContact?.name}
          onChange={(value, errorMessage) => {
            onFieldChange("primaryContact.name")(value, errorMessage);
          }}
          validator={validate(required(), minLength(2))}
        />
        <Field
          type="text"
          id="primaryContact.email"
          labelText="Primary Email"
          placeholder="Enter email"
          isRequired={true}
          isDisabled={isSubmitting}
          value={formValues.primaryContact?.email}
          error={errors.primaryContact?.email}
          onChange={(value, errorMessage) => {
            onFieldChange("primaryContact.email")(value, errorMessage);
          }}
          validator={validate(required(), email())}
        />
        <Field
          type="text"
          id="domain"
          labelText="Domain"
          placeholder="Enter domain"
          isMultiValue={true}
          isRequired={true}
          isDisabled={isSubmitting}
          value={formValues.domain || []}
          error={errors.domain}
          onChange={(value, errorMessage) => {
            onFieldChange("domain")(value, errorMessage);
          }}
          setIsFormDisabled={setIsFormDisabled}
          validator={validate(
            objectOrArrayRequired(),
            domain(),
            noDuplicateArrayItems()
          )}
        />
      </Flex>

      <Flex direction="column" gap="2px">
        <Field
          id="customer_segment"
          type="combobox"
          labelText="Customer Segment"
          labelHint={<CustomerSegmentTooltip listStyle={{ padding: 5 }} />}
          placeholder="Please Select"
          clickable={true}
          isRequired
          isDisabled={isSubmitting}
          showSkeleton={isLoadingSegments}
          value={formValues.customer_segment}
          onChange={onFieldChange("customer_segment")}
          options={getCustomerSegmentOptions}
        />

        {isMedicareLicensable && (
          <Field
            type="radio"
            id="medicare_licensed"
            labelText="Is Medicare QE Included?"
            isMultiValue={false}
            isDisabled={isSubmitting}
            //   isRequired={true}    // Removing isRequired will fix the issue created which a Yes to No change causes, leading !value to be equal to true, that results in displaying the "Required" error
            value={formValues.medicare_licensed}
            onChange={onFieldChange("medicare_licensed")}
            options={[
              { label: "Yes", value: true },
              { label: "No", value: false },
            ]}
          />
        )}
      </Flex>

      {/* Action buttons */}
      <Flex direction="row" gap="16px" justifyContent="flex-end">
        <Button
          variant="outlined"
          width="auto"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          width="auto"
          onClick={onFormSubmit}
          disabled={isDisabled}
          isLoading={isSubmitting}
        >
          Next
        </Button>
      </Flex>
    </Flex>
  );
};

const userLicensesFormSchema = z.object({
  licenses: z.object({
    tableau_user_licenses: z.object({
      explorer_count: z.number(),
      viewer_count: z.number(),
      creator_count: z.number().optional(),
    }),
    user_limit: z.number(),
    analytics_hub_user_licenses: z.object({
      pro_count: z.number(),
    }),
  }),
});

const UserLicensesForm = ({
  onSubmit,
  defaultValues,
  isFetching = false,
  isSubmitting,
  disabledUntilTouched = false,
  error,
}) => {
  const params = useParams();

  const { orgId: organizationId } = params;
  const { formValues, errors, onFormSubmit, onFieldChange, isDisabled } =
    useForm({
      initialValues: defaultValues,
      disabledUntilTouched,
      onSubmit,
      formSchema: userLicensesFormSchema,
      isFetchingInitialValues: isFetching,
    });

  return (
    <Flex direction="column" gap="2px">
      <Error>{error}</Error>
      <Field
        id="licenses.user_limit"
        type="number"
        min="0"
        labelText="Member Limit"
        placeholder="Enter number"
        showSkeleton={isFetching}
        value={formValues?.licenses?.user_limit}
        isDisabled={isSubmitting}
        onChange={onFieldChange("licenses.user_limit")}
        validator={validate()}
        error={errors.licenses?.user_limit}
      />
      <Field
        id="licenses.analytics_hub_user_licenses.pro_count"
        type="number"
        min="0"
        labelText="Analytics Hub Pro Limit"
        placeholder="Enter number"
        showSkeleton={isFetching}
        value={
          formValues?.licenses?.analytics_hub_user_licenses?.pro_count ?? 0
        }
        isDisabled={isSubmitting}
        onChange={onFieldChange(
          "licenses.analytics_hub_user_licenses.pro_count"
        )}
        validator={validate()}
        error={errors.licenses?.analytics_hub_user_licenses?.pro_count}
      />
      <Field
        id="licenses.tableau_user_licenses.explorer_count"
        type="number"
        min="0"
        labelText="BI Portal Explorer"
        placeholder="Enter number"
        showSkeleton={isFetching}
        value={formValues?.licenses?.tableau_user_licenses?.explorer_count}
        isDisabled={isSubmitting}
        onChange={onFieldChange(
          "licenses.tableau_user_licenses.explorer_count"
        )}
        validator={validate()}
        error={errors.licenses?.tableau_user_licenses?.explorer_count}
      />
      <Field
        id="licenses.tableau_user_licenses.viewer_count"
        type="number"
        min="0"
        labelText="BI Portal Viewer"
        placeholder="Enter number"
        isRequired
        showSkeleton={isFetching}
        value={formValues?.licenses?.tableau_user_licenses.viewer_count}
        isDisabled={isSubmitting}
        onChange={onFieldChange("licenses.tableau_user_licenses.viewer_count")}
        validator={validate()}
        error={errors.licenses?.tableau_user_licenses?.viewer_count}
      />

      {organizationId === "populi" && (
        <Field
          id="licenses.tableau_user_licenses.creator_count"
          type="number"
          min="0"
          labelText="BI Portal Creator"
          placeholder="Enter number"
          showSkeleton={isFetching}
          value={
            formValues?.licenses?.tableau_user_licenses?.creator_count ?? 0
          }
          isDisabled={isSubmitting}
          onChange={onFieldChange(
            "licenses.tableau_user_licenses.creator_count"
          )}
          validator={validate()}
          error={errors.licenses?.tableau_user_licenses?.creator_count}
        />
      )}

      {/* Action buttons */}
      <Flex direction="row" gap="16px" justifyContent="flex-end">
        <Button
          variant="contained"
          width="auto"
          onClick={onFormSubmit}
          disabled={isSubmitting || (!!defaultValues?.id && isDisabled)}
          isLoading={isSubmitting}
        >
          Finish
        </Button>
      </Flex>
    </Flex>
  );
};

const CreateOrganizationPanel = () => {
  const [isOpen, setOpen] = useState(true);
  const queryClient = useQueryClient();

  const [activeStep, setActiveStep] = useState(0);
  const [newOrgId, setNewOrgId] = useState(null);

  const createOrg = useAuthenticatedMutation(createOrganization);

  const updateLicenses = useAuthenticatedMutation((req) =>
    updateOrganizationLicenses({ ...req, organizationId: newOrgId })
  );

  const createOrgMutation = useMutation({
    mutationFn: createOrg,
    onSuccess: (data, newOrg, context) => {
      // Remember org id
      setNewOrgId(newOrg.id);

      // Go to next step
      setActiveStep(1);

      // Add org to cached query results
      queryClient.setQueryData(["organizations"], (cachedList) =>
        [
          {
            isUpdated: true,
            userCount: {
              explorer: 0,
              viewer: 0,
              creator: 0,
              total: 0,
            },
            ...newOrg,
          },
        ].concat(cachedList || [])
      );

      // Invalidate cached query results
      queryClient.invalidateQueries({ queryKey: ["organizations"] });

      // Notify
      toast.success("Organization has been created.");
    },
  });

  const licenseUpdatesMutation = useMutation({
    mutationFn: updateLicenses,
    onSuccess: (data, updatedOrg) => {
      // Update cached query results
      queryClient.setQueryData(["organizations"], (cachedList) => {
        if (!cachedList) {
          return [];
        }
        return cachedList.map((cachedOrg) =>
          cachedOrg.id === newOrgId
            ? { ...cachedOrg, updatedOrg, isUpdated: true }
            : cachedOrg
        );
      });

      // Invalidate cached query results
      queryClient.invalidateQueries({ queryKey: ["organizations"] });

      // Close form
      setOpen(false);

      // Notify
      toast.success("User licenses have been set.");
    },
  });

  const steps = [
    {
      form: (
        <OrgDetailsForm
          onSubmit={createOrgMutation.mutateAsync}
          error={createOrgMutation.error}
          isSubmitting={createOrgMutation.isPending}
          initialValues={DEFAULT_ORG}
          onClose={() => setOpen(false)}
        />
      ),
    },
    {
      form: (
        <UserLicensesForm
          onSubmit={licenseUpdatesMutation.mutateAsync}
          error={licenseUpdatesMutation.error}
          isSubmitting={licenseUpdatesMutation.isPending}
          disabledUntilTouched={false}
          defaultValues={DEFAULT_ORG}
        />
      ),
    },
  ];

  return (
    <Panel isOpen={isOpen} onOpenChange={setOpen} onCloseRedirectTo="../">
      <PanelHeader>
        <PanelTitle>Create Organization</PanelTitle>
      </PanelHeader>
      <PanelContent>{steps[activeStep].form}</PanelContent>
    </Panel>
  );
};

export default CreateOrganizationPanel;
